import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import { Calendar } from 'primereact/calendar'
import { getCalendar as getCalendarOptions, useWindowResize } from '@lux/wardrobe-lib'
import { addLocale, localeOption, updateLocaleOption } from 'primereact/api'

const dateFormat = 'MM/DD/YYYY'

const CalendarActivitiesPicker = (props: {
    userLanguage: string
    showFullCalendar: boolean
    setShowFullCalendar: (showFullCalendar: boolean) => void
    setCurrentDate: (currentDate: Date) => void
    setCalendarHeight: (calendarHeight: number) => void
}) => {
    const { userLanguage, setCurrentDate, setCalendarHeight, showFullCalendar, setShowFullCalendar } = props

    const intl = useIntl()

    const showShortDate = useWindowResize(1486)

    const calendarActivitiesPickerClass = 'home-calendar-activities-picker'
    const calendarActivitiesPickerHeaderClass = `${calendarActivitiesPickerClass}-header`

    const [dateSelected, setDateSelected] = useState(new Date())


    const momentDateSelected = moment(dateSelected)

    const dateConfig = {
        minDate: new Date(momentDateSelected.clone().startOf('week').format(dateFormat)),
        maxDate: new Date(momentDateSelected.clone().endOf('week').format(dateFormat)),
    }

    
    useEffect(() => {
        setCurrentDate(dateSelected)
    }, [dateSelected])

    const goNextWeek = () => {
        setDateSelected((value) => new Date(moment(value).add(1, 'week').format(dateFormat)))
    }

    const goPrevWeek = () => {
        setDateSelected((value) => new Date(moment(value).subtract(1, 'week').format(dateFormat)))
    }

    useEffect(() => {
        let calendar = document.getElementsByClassName(
            'home-calendar-activities-picker'
        )[0] as HTMLElement
        setCalendarHeight(calendar.offsetHeight)
    }, [showFullCalendar])

    useEffect(() => {
        if (showFullCalendar) {
            let prev = document.getElementsByClassName('pi-chevron-left')[0] as HTMLElement
            let next = document.getElementsByClassName('pi-chevron-right')[0] as HTMLElement
            prev?.classList.add('cu')
            prev?.classList.add('cu-caret-left')
            prev?.classList.remove('pi')
            prev?.classList.remove('pi-chevron-left')
            next?.classList.add('cu')
            next?.classList.add('cu-caret-right')
            next?.classList.remove('pi')
            next?.classList.remove('pi-chevron-right')
        }
    }, [dateSelected, showFullCalendar])

    const headerTemplate = () => {
        return (
            <div className={calendarActivitiesPickerHeaderClass}>
                <span className={`${calendarActivitiesPickerHeaderClass}__title-container`}>
                    <h2>
                        {momentDateSelected.format(showShortDate ? 'ddd' : 'dddd')},{' '}
                        {momentDateSelected.format('DD')}
                    </h2>
                    <h2>{momentDateSelected.format('MMM')}</h2>
                </span>
                {!showFullCalendar && (
                    <span className={`${calendarActivitiesPickerHeaderClass}__cta-container`}>
                        <button type="button" className="p-datepicker-prev" onClick={goPrevWeek}>
                            <span className="p-datepicker-prev-icon cu cu-caret-left"></span>
                        </button>
                        <button type="button" className="p-datepicker-next" onClick={goNextWeek}>
                            <span className="p-datepicker-next-icon cu cu-caret-right"></span>
                        </button>
                    </span>
                )}
            </div>
        )
    }


    const onDayChange = (newDateSelected) => {
        setDateSelected(new Date(moment(newDateSelected).format(dateFormat)))
    }

    const onMonthChange = (e: any) => {
        const currentDayMonth = dateSelected.getDate();
        const dateMoment = moment(e.value);
        const nextDate = new Date(dateMoment.date(currentDayMonth).format(dateFormat));
        setDateSelected(nextDate)
    }

    const footerHandler = () => {
        setShowFullCalendar(!showFullCalendar)
    }

    const enumerateDaysBetweenDates = (startDate, endDate) => {
        var dates = []
        var currDate = moment(startDate)
        var lastDate = moment(endDate)
        dates.push(currDate.clone())
        while (currDate.add(1, 'days').diff(lastDate) < 0) {
            dates.push(moment(currDate.toDate()))
        }
        dates.push(lastDate)
        return dates
    }

    const [checkLocale, setCheckLocale] = useState<boolean>(false)

    useEffect(() => {
        let name = userLanguage ? userLanguage?.split('-')[0] : 'en'
        let options = getCalendarOptions(intl)
        addLocale(name, options)
        updateLocaleOption('dayNamesMin', localeOption('dayNamesShort', name), name)
        setCheckLocale(true)
    }, [userLanguage])

    return (
        <div className={calendarActivitiesPickerClass}>
            {!showFullCalendar && !!dateConfig.minDate && !!dateConfig.maxDate && (
                <div className={`${calendarActivitiesPickerClass}__week-mode`}>
                    <div className={`${calendarActivitiesPickerClass}__week-mode-header-container`}>
                        {headerTemplate()}
                    </div>
                    <div className={`${calendarActivitiesPickerClass}__week-mode-days-container`}>
                        {enumerateDaysBetweenDates(dateConfig.minDate, dateConfig.maxDate).map(
                            (date, index) => (
                                <div
                                    key={index}
                                    className={`${calendarActivitiesPickerClass}__week-mode-day-container`}>
                                    <div
                                        className={`${calendarActivitiesPickerClass}__week-mode-day-word`}>
                                        {date.format('ddd')}
                                    </div>
                                    <div
                                        onClick={() => onDayChange(new Date(date))}
                                        className={`${calendarActivitiesPickerClass}__week-mode-day ${
                                            date.isSame(momentDateSelected.format(dateFormat))
                                                ? 'highlight'
                                                : ''
                                        }`}
                                        key={index}>
                                        {date.format('D')}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                    <div
                        className="calendar-expander-container week">
                        <button
                            onClick={footerHandler}
                            className="cu cu-caret-down">
                        </button>
                    </div>
                </div>
            )}
            {showFullCalendar && (
                <>
                    <Calendar
                        inline
                        viewDate={dateSelected}
                        value={dateSelected}
                        headerTemplate={headerTemplate}
                        onViewDateChange={onMonthChange}
                        onChange={(e) => onDayChange(e.value)}
                        locale={checkLocale ? userLanguage?.split('-')[0] : 'en'}
                    />
                    <div
                        className="calendar-expander-container">
                        <button
                            onClick={footerHandler}
                            className="cu cu-caret-up">
                        </button>
                    </div>
                </>
            )}
        </div>
    )
}

export default CalendarActivitiesPicker
